import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { map, Observable, of, switchMap } from 'rxjs';
import { ProjectFinancementDetailsDto } from 'app/modules/models/projectFinancement.type';
import { CreateClassicProjectRequest, CreatePrepareProjectRequest } from '../projectFinancementBody';
import { DraftProjectFinancementDto } from 'app/modules/models/draftProjectFinancement.type';

@Injectable({
  providedIn: 'root'
})
export class CreationProjetService {
  protected apiRoute = environment.RESOURCE_API;

  constructor(private _httpClient: HttpClient) { }

  depotProjectFinancement(
    companyId: string,
    partnerId: string,
    projectFinancementFilesBody: any,
    mandateId?: string | null,
    isImportedSignedDevis: boolean = false
  ): Observable<any> {
    const baseUrl =
      this.apiRoute +
      environment.apiCatalog.projectFinancement.controllerRoute +
      environment.apiCatalog.projectFinancement.subRoutes.depotProjectFinancement;

    // Construction dynamique des query params
    let params = new HttpParams()
      .set('companyId', companyId)
      .set('partnerId', partnerId)
      .set('isImportedSignedDevis', isImportedSignedDevis);

    if (mandateId) {
      params = params.set('mandateId', mandateId);
    }

    return this._httpClient.post<any>(baseUrl, projectFinancementFilesBody, {
      params,
      headers: new HttpHeaders({
      }),
    });
  }

  createPrepareProject(request: CreatePrepareProjectRequest
  ): Observable<any> {
    const baseUrl =
      this.apiRoute +
      environment.apiCatalog.projectFinancement.controllerRoute +
      environment.apiCatalog.projectFinancement.subRoutes.createPrepareProject;

    // Construction dynamique des query params
    let params = new HttpParams()
      .set('companyId', request.companyId)
      .set('partnerId', request.partnerId)
      .set('partnerName', request.partnerName || '')
      .set('isImportedSignedDevis', request.isImportedSignedDevis);


    if (request.mandateId) {
      params = params.set('mandateId', request.mandateId);
      params = params.set('mandateName', request.mandateName || '');
    }

    const formDataToSubmit = request.projectFinancementFilesBody || new FormData();

    if (request.projectFinancementOperations && request.projectFinancementOperations.length > 0) {
      formDataToSubmit.append(
        'ProjectFinancementOperations',
        JSON.stringify(request.projectFinancementOperations)
      );
    }

    return this._httpClient.post<any>(baseUrl, formDataToSubmit, {
      params,
      headers: new HttpHeaders({
      }),
    });
  }

  createClassicProjectFinancementTera(request: CreateClassicProjectRequest
  ): Observable<any> {
    const baseUrl =
      this.apiRoute +
      environment.apiCatalog.projectFinancement.controllerRoute +
      environment.apiCatalog.projectFinancement.subRoutes.createClassicProjectFinancementTera;

    // Construction dynamique des query params
    let params = new HttpParams()
      .set('companyId', request.companyId)
      .set('partnerId', request.partnerId)
      .set('partnerName', request.partnerName || '')
      .set('isImportedSignedDevis', request.isImportedSignedDevis);


    if (request.mandateId) {
      params = params.set('mandateId', request.mandateId);
      params = params.set('mandateName', request.mandateName || '');
    }

    const formDataToSubmit = request.projectFinancementFilesBody || new FormData();

    if (request.projectFinancementOperations && request.projectFinancementOperations.length > 0) {
      formDataToSubmit.append(
        'ProjectFinancementOperations',
        JSON.stringify(request.projectFinancementOperations)
      );
    }

    return this._httpClient.post<any>(baseUrl, formDataToSubmit, {
      params,
      headers: new HttpHeaders({
      }),
    });
  }

  saveDovaAnalysis(
    projectId: string,
    companyId: string,
    partnerId: string,
    importedSignedDevis: boolean,
    projectFinancementFilesBody: any,
  ): Observable<any> {
    const baseUrl =
      this.apiRoute +
      environment.apiCatalog.projectFinancement.controllerRoute +
      environment.apiCatalog.projectFinancement.subRoutes.saveDovaAnalysis;

    // Construction dynamique des query params
    let params = new HttpParams()
      .set('projectId', projectId)
      .set('companyId', companyId)
      .set('partnerId', partnerId)
      .set('importedSignedDevis', importedSignedDevis);

    return this._httpClient.post<any>(baseUrl, projectFinancementFilesBody, {
      params,
      headers: new HttpHeaders({
      }),
    });
  }

  depotProjectFinancementDova(
    projectId: string,
    projectFinancementFilesBody: any,
  ): Observable<any> {
    const baseUrl =
      this.apiRoute +
      environment.apiCatalog.projectFinancement.controllerRoute +
      environment.apiCatalog.projectFinancement.subRoutes.depotProjectFinancementDova + projectId;

    return this._httpClient.put<any>(baseUrl, projectFinancementFilesBody, {
      headers: new HttpHeaders({
      }),
    });
  }

  depotDocumentProcessingDova(
    projectId: string,
    userId: string,
    projectFinancementFilesBody: any,
  ): Observable<any> {
    const baseUrl =
      this.apiRoute +
      environment.apiCatalog.projectFinancement.controllerRoute +
      environment.apiCatalog.projectFinancement.subRoutes.depotDocumentProcessingDova + projectId;

    let params = new HttpParams();
    if (userId) {
      params = params.set('userId', userId);
    }
    return this._httpClient.put<any>(baseUrl, projectFinancementFilesBody, {
      params,
      headers: new HttpHeaders({
      }),
    });
  }

  updateProjectFinancementDetail(
    projectId: string,
    projectFinancementDetails: ProjectFinancementDetailsDto,
  ): Observable<any> {
    const baseUrl =
      this.apiRoute +
      environment.apiCatalog.projectFinancement.controllerRoute +
      environment.apiCatalog.projectFinancement.subRoutes.updateProjectFinancementDetail + projectId;

    return this._httpClient.put<any>(baseUrl, projectFinancementDetails, {
      headers: new HttpHeaders({
      }),
    });
  }

  getPendingDraftProjects(companyId: string, partnerId: string): Observable<DraftProjectFinancementDto[]> {
    const baseUrl =
      this.apiRoute +
      environment.apiCatalog.projectFinancement.controllerRoute +
      environment.apiCatalog.projectFinancement.subRoutes.getPendingDraftProjects;

    let params = new HttpParams()
      .set('companyId', companyId)
      .set('partnerId', partnerId);

    return this._httpClient.get<DraftProjectFinancementDto[]>(baseUrl, { params });
  }

  getDraftProject(projectId: string): Observable<DraftProjectFinancementDto> {
    const baseUrl =
      this.apiRoute +
      environment.apiCatalog.projectFinancement.controllerRoute +
      environment.apiCatalog.projectFinancement.subRoutes.getDraftProject + projectId;

    return this._httpClient.get<DraftProjectFinancementDto>(baseUrl);
  }

  createBatchDraftProject(
    companyId: string,
    partnerId: string,
    mandateId: string | null,
    partnerName: string,
    formData: FormData,
    userId?: string | null
  ): Observable<any> {
    const baseUrl =
      this.apiRoute +
      environment.apiCatalog.projectFinancement.controllerRoute +
      environment.apiCatalog.projectFinancement.subRoutes.createBatchDraftProject;

    let params = new HttpParams()
      .set('companyId', companyId)
      .set('partnerId', partnerId)
      .set('partnerName', partnerName);

    if (mandateId) {
      params = params.set('mandateId', mandateId);
    }

    if (userId) {
      params = params.set('userId', userId);
    }

    return this._httpClient.post<any>(baseUrl, formData, { params });
  }

  deleteDraftProjects(projectIds: string[]): Observable<boolean> {
    const baseUrl =
      this.apiRoute +
      environment.apiCatalog.projectFinancement.controllerRoute +
      environment.apiCatalog.projectFinancement.subRoutes.deleteDraftProjects;

    return this._httpClient.post<boolean>(baseUrl, projectIds);
  }

  verifyDraftProjectDova(projectId: string, userId: string): Observable<any> {
    const baseUrl =
      this.apiRoute +
      environment.apiCatalog.projectFinancement.controllerRoute +
      environment.apiCatalog.projectFinancement.subRoutes.verifyDraftProjectDova + projectId;

    let params = new HttpParams();
    if (userId) {
      params = params.set('userId', userId);
    }

    return this._httpClient.post<any>(baseUrl, null, { params });
  }

  replaceDraftProjectDevisAndVerifyDova(projectId: string, userId: string, formData: FormData): Observable<any> {
    const baseUrl =
      this.apiRoute +
      environment.apiCatalog.projectFinancement.controllerRoute +
      environment.apiCatalog.projectFinancement.subRoutes.replaceDraftProjectDevisAndVerifyDova + projectId;

    let params = new HttpParams();
    if (userId) {
      params = params.set('userId', userId);
    }

    return this._httpClient.put<any>(baseUrl, formData, { params });
  }

  submitBatchDraftProjectTera(projectId: string, request: any): Observable<any> {
    const baseUrl =
      this.apiRoute +
      environment.apiCatalog.projectFinancement.controllerRoute +
      environment.apiCatalog.projectFinancement.subRoutes.submitBatchDraftProjectTera + projectId;

    let params = new HttpParams()
      .set('companyId', request.companyId)
      .set('partnerId', request.partnerId)
      .set('partnerName', request.partnerName || '');

    if (request.mandateId) {
      params = params.set('mandateId', request.mandateId);
      params = params.set('mandateName', request.mandateName || '');
    }

    const formDataToSubmit = request.projectFinancementFilesBody || new FormData();

    if (request.projectFinancementOperations && request.projectFinancementOperations.length > 0) {
      formDataToSubmit.append(
        'ProjectFinancementOperations',
        JSON.stringify(request.projectFinancementOperations)
      );
    }

    return this._httpClient.post<any>(baseUrl, formDataToSubmit, { params });
  }

  submitBatchDraftProjectDova(projectId: string, request: any): Observable<any> {
    const baseUrl =
      this.apiRoute +
      environment.apiCatalog.projectFinancement.controllerRoute +
      environment.apiCatalog.projectFinancement.subRoutes.submitBatchDraftProjectDova + projectId;

    let params = new HttpParams()
      .set('companyId', request.companyId)
      .set('partnerId', request.partnerId)
      .set('userId', request.userId)
      .set('partnerName', request.partnerName || '');

    if (request.mandateId) {
      params = params.set('mandateId', request.mandateId);
      params = params.set('mandateName', request.mandateName || '');
    }

    const formDataToSubmit = request.projectFinancementFilesBody || new FormData();

    if (request.projectFinancementOperations && request.projectFinancementOperations.length > 0) {
      formDataToSubmit.append('ProjectFinancementOperations', JSON.stringify(request.projectFinancementOperations));
    }

    return this._httpClient.post<any>(baseUrl, formDataToSubmit, { params });
  }

  saveBatchDovaAnalysis(projectId: string, dovaAnalysisJson: string): Observable<boolean> {
    const baseUrl =
      this.apiRoute +
      environment.apiCatalog.projectFinancement.controllerRoute +
      environment.apiCatalog.projectFinancement.subRoutes.saveBatchDovaAnalysis + projectId;

    return this._httpClient.post<boolean>(baseUrl, JSON.stringify(dovaAnalysisJson), {
      headers: { 'Content-Type': 'application/json' }
    });
  }

  checkIfProjectExtractionIsSaved(projectId: string): Observable<boolean> {
    // The legacy existence check can succeed before extracted fields are saved.
    // Require an explicit persisted false; a missing flag must keep us waiting.
    return this.checkIfProjectExists(projectId).pipe(
      switchMap(exists => {
        if (exists !== true) return of(false);

        const route = this.apiRoute + environment.apiCatalog.projectFinancement.controllerRoute
          + environment.apiCatalog.projectFinancement.subRoutes.getProjectFinancementById + projectId;
        return this._httpClient.get<ProjectFinancementDetailsDto>(route).pipe(
          map(details => details?.projectFinancement?.isAwaitingDovaResponse === false)
        );
      })
    );
  }

  checkIfProjectExists(projectId: string): Observable<boolean> {
    const route = this.apiRoute + environment.apiCatalog.projectFinancement.controllerRoute + environment.apiCatalog.projectFinancement.subRoutes.checkIfProjectExists + projectId;
    return this._httpClient.get<boolean>(route, {
      headers: {
        // Do NOT set 'Content-Type'; let the browser set it with boundary
      }
    });
  }
}
